// ==========================================
// Store d'authentification - Zustand
// ==========================================

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@shared/types';
import { disconnectSocket } from '../hooks/useSocket';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setTokenAndFetchUser: (token: string) => Promise<boolean>;
  login: (email: string, password: string) => Promise<boolean>;
  register: (email: string, password: string, pseudo: string) => Promise<boolean>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateProfile: (data: Partial<User>) => Promise<boolean>;
  clearError: () => void;
}

const API_URL = '/api';

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,
      error: null,
      
      setUser: (user) => set({ user, isAuthenticated: !!user }),
      
      setToken: (token) => set({ token }),
      
      // OAuth callback - set token and fetch user info
      setTokenAndFetchUser: async (token: string) => {
        set({ token, isLoading: true });
        
        try {
          const res = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          const data = await res.json();
          
          if (data.success && data.data) {
            set({
              user: data.data,
              isAuthenticated: true,
              isLoading: false,
            });
            return true;
          } else {
            set({
              token: null,
              isLoading: false,
            });
            return false;
          }
        } catch (error) {
          set({ token: null, isLoading: false });
          return false;
        }
      },
      
      clearError: () => set({ error: null }),
      
      login: async (email, password) => {
        set({ isLoading: true, error: null });
        
        try {
          const res = await fetch(`${API_URL}/auth/login`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password }),
          });
          
          const data = await res.json();
          
          if (!data.success) {
            set({ isLoading: false, error: data.error || 'Erreur de connexion' });
            return false;
          }
          
          set({
            user: data.data.user,
            token: data.data.token,
            isAuthenticated: true,
            isLoading: false,
          });
          
          return true;
        } catch (error) {
          set({ isLoading: false, error: 'Erreur de connexion au serveur' });
          return false;
        }
      },
      
      register: async (email, password, pseudo) => {
        set({ isLoading: true, error: null });
        
        try {
          const res = await fetch(`${API_URL}/auth/register`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email, password, pseudo }),
          });
          
          const data = await res.json();
          
          if (!data.success) {
            set({ isLoading: false, error: data.error || 'Erreur d\'inscription' });
            return false;
          }
          
          set({
            user: data.data.user,
            token: data.data.token,
            isAuthenticated: true,
            isLoading: false,
          });
          
          return true;
        } catch (error) {
          set({ isLoading: false, error: 'Erreur de connexion au serveur' });
          return false;
        }
      },
      
      logout: () => {
        const { token } = get();

        if (token) {
          fetch(`${API_URL}/auth/logout`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
          }).catch(() => {});
        }

        disconnectSocket();

        set({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
        });
      },
      
      checkAuth: async () => {
        const { token } = get();
        
        if (!token) {
          set({ isLoading: false });
          return;
        }
        
        try {
          const res = await fetch(`${API_URL}/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          
          const data = await res.json();
          
          if (data.success && data.data) {
            set({
              user: data.data,
              isAuthenticated: true,
              isLoading: false,
            });
          } else {
            set({
              user: null,
              token: null,
              isAuthenticated: false,
              isLoading: false,
            });
          }
        } catch (error) {
          set({ isLoading: false });
        }
      },
      
      updateProfile: async (data) => {
        const { token, user } = get();
        
        if (!token || !user) return false;
        
        try {
          const res = await fetch(`${API_URL}/auth/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(data),
          });
          
          const result = await res.json();
          
          if (result.success) {
            set({ user: { ...user, ...data } });
            return true;
          }
          
          return false;
        } catch (error) {
          return false;
        }
      },
    }),
    {
      name: 'geekquiz-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
